import { ACCEPTED_AUDIO_CONTENT_TYPES } from '@common/constants/contentTypes'
import times from 'lodash/times'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { ChooseFile } from '@/modules/chatting/ui/components/ChooseFile'

import { DialogProps } from '../types'
import { AudioListItem } from './AudioListItem'
import { type UseAddAudioParams, useAddAudio } from './useAddAudio'

interface Props extends Pick<UseAddAudioParams, 'onAddAudio'>, DialogProps {}

export const AddAudioDialog = (props: Props) => {
  const {
    audios,
    isLoading,
    isError,
    selectedAudio,
    isAdding,
    refetch,
    handleAdd,
    handleSelect,
    handleCloseModal,
    getAudioFile,
    handleSetFile,
  } = useAddAudio(props)
  const { isOpen, onClose } = props

  return (
    <Dialog
      show={isOpen}
      bigDialog
      afterLeave={handleCloseModal}
      onClose={onClose}
      panelClassName="lg:max-w-[533px]"
    >
      <Dialog.Header>
        <Dialog.Title>Add Audio</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description className="mt-[8px] block text-center">
        To send your own audios you have to drop them here or select recently
        used audios below
      </Dialog.Description>
      <Dialog.Description className="mt-6 block space-y-3">
        <ChooseFile
          setFile={handleSetFile}
          acceptedContentTypes={ACCEPTED_AUDIO_CONTENT_TYPES}
        />
        <div className="max-h-[325px] space-y-4 overflow-y-auto">
          {isLoading ? (
            times(3).map((num) => (
              <Skeleton
                key={num}
                className="h-[62px] w-full rounded-md dark:bg-card"
              />
            ))
          ) : isError ? (
            <div className="py-4">
              <p className="text-center">
                Something went wrong while loading audios
              </p>
              <Button
                className="mt-2 mx-auto block"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </div>
          ) : (
            audios?.map((audio) => (
              <AudioListItem
                audio={audio}
                key={audio.id}
                onClick={() => handleSelect(audio)}
                isSelected={selectedAudio?.id === audio.id}
                getAudioFile={getAudioFile}
              />
            ))
          )}
        </div>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          onClick={handleAdd}
          disabled={!selectedAudio}
          isLoading={isAdding}
        >
          Add Audio
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
